import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  getBrandingSettings,
  getLegalFooterSettings,
  updateBrandingSettings,
  updateLegalFooterSettings,
  uploadBrandingLogo,
} from '../services/compliance-settings.service';
import type {
  BrandingSettings,
  LegalFooterSettings,
  UpdateBrandingSettingsInput,
} from '../types/compliance-settings.types';

export const BRANDING_QUERY_KEY = ['settings', 'branding'] as const;
export const FOOTER_QUERY_KEY = ['settings', 'footer'] as const;

export const useGetBrandingSettings = () =>
  useGlobalQuery<BrandingSettings>({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: getBrandingSettings,
    staleTime: 30_000,
  });

export const useUpdateBrandingSettings = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<BrandingSettings, Error, UpdateBrandingSettingsInput>({
    mutationFn: updateBrandingSettings,
    onSuccess: (settings) => queryClient.setQueryData(BRANDING_QUERY_KEY, settings),
    suppressToast: true,
  });
};

export const useUploadBrandingLogo = () =>
  useGlobalMutation<{ itemId: string; url: string }, Error, File>({
    mutationFn: uploadBrandingLogo,
    suppressToast: true,
  });

export const useGetLegalFooterSettings = () =>
  useGlobalQuery<LegalFooterSettings>({
    queryKey: FOOTER_QUERY_KEY,
    queryFn: getLegalFooterSettings,
    staleTime: 30_000,
  });

export const useUpdateLegalFooterSettings = () => {
  const queryClient = useQueryClient();
  return useGlobalMutation<LegalFooterSettings, Error, LegalFooterSettings>({
    mutationFn: updateLegalFooterSettings,
    onSuccess: (settings) => queryClient.setQueryData(FOOTER_QUERY_KEY, settings),
    suppressToast: true,
  });
};
